import { useRef, useEffect, useState, useCallback } from 'react'
import * as Y from 'yjs'
import {
  Plus,
  GripVertical,
  Trash2,
  Zap,
  MousePointerClick,
  Layers,
  Shield,
  Globe,
  Flame,
  X,
  Sparkles,
  Info,
} from 'lucide-react'
import ArtifactStateOverlay from './ArtifactStateOverlay'
import FallbackNotice from './ui/FallbackNotice'
import type { UserPresence } from '../types'
import { generateId } from '../lib/id'

interface EventStormElement {
  id: string
  type: 'domain_event' | 'command' | 'aggregate' | 'policy' | 'external_system' | 'hotspot'
  text: string
  x: number
  y: number
  lane: string
  color?: string
  created_by: string
  created_at: string
}

const LANES = [
  {
    id: 'domain_event',
    label: 'Domain Events',
    short: 'Event',
    icon: Zap,
    color: '#FF8C00',
    bg: 'bg-[#FFF3E0] dark:bg-[#4A2500]/30',
    headerBg: 'bg-[#FF8C00]',
    border: 'border-[#FF8C00]/25 dark:border-[#FF8C00]/25',
    text: 'text-[#4A2500] dark:text-[#FFF3E0]',
    hint: 'past tense',
  },
  {
    id: 'command',
    label: 'Commands',
    short: 'Command',
    icon: MousePointerClick,
    color: '#0066CC',
    bg: 'bg-[#E3F2FD] dark:bg-[#0D2847]/30',
    headerBg: 'bg-[#0066CC]',
    border: 'border-[#0066CC]/25 dark:border-[#0066CC]/25',
    text: 'text-[#0D2847] dark:text-[#E3F2FD]',
    hint: 'imperative',
  },
  {
    id: 'aggregate',
    label: 'Aggregates',
    short: 'Aggregate',
    icon: Layers,
    color: '#FFD700',
    bg: 'bg-[#FFFDE7] dark:bg-[#4A4000]/30',
    headerBg: 'bg-[#FFD700]',
    border: 'border-[#FFD700]/25 dark:border-[#FFD700]/25',
    text: 'text-[#4A4000] dark:text-[#FFFDE7]',
    hint: '',
  },
  {
    id: 'policy',
    label: 'Policies',
    short: 'Policy',
    icon: Shield,
    color: '#9370DB',
    bg: 'bg-[#F3E5F5] dark:bg-[#311B4D]/30',
    headerBg: 'bg-[#9370DB]',
    border: 'border-[#9370DB]/25 dark:border-[#9370DB]/25',
    text: 'text-[#311B4D] dark:text-[#F3E5F5]',
    hint: '',
  },
  {
    id: 'external_system',
    label: 'External Systems',
    short: 'External',
    icon: Globe,
    color: '#FF69B4',
    bg: 'bg-[#FCE4EC] dark:bg-[#4A1528]/30',
    headerBg: 'bg-[#FF69B4]',
    border: 'border-[#FF69B4]/25 dark:border-[#FF69B4]/25',
    text: 'text-[#4A1528] dark:text-[#FCE4EC]',
    hint: '',
  },
  {
    id: 'hotspot',
    label: 'Hotspots',
    short: 'Hotspot',
    icon: Flame,
    color: '#FF1493',
    bg: 'bg-[#FCE4EC] dark:bg-[#4A0018]/30',
    headerBg: 'bg-[#FF1493]',
    border: 'border-[#FF1493]/25 dark:border-[#FF1493]/25',
    text: 'text-[#4A0018] dark:text-[#FCE4EC]',
    hint: '',
  },
]

const NOTE_BG: Record<string, string> = {
  domain_event: 'bg-[#FFF3E0] dark:bg-[#4A2500]',
  command: 'bg-[#E3F2FD] dark:bg-[#0D2847]',
  aggregate: 'bg-[#FFFDE7] dark:bg-[#4A4000]',
  policy: 'bg-[#F3E5F5] dark:bg-[#311B4D]',
  external_system: 'bg-[#FCE4EC] dark:bg-[#4A1528]',
  hotspot: 'bg-[#FCE4EC] dark:bg-[#4A0018]',
}

const NOTE_FOLD: Record<string, string> = {
  domain_event: 'border-b-[#FFF3E0] dark:border-b-[#4A2500]',
  command: 'border-b-[#E3F2FD] dark:border-b-[#0D2847]',
  aggregate: 'border-b-[#FFFDE7] dark:border-b-[#4A4000]',
  policy: 'border-b-[#F3E5F5] dark:border-b-[#311B4D]',
  external_system: 'border-b-[#FCE4EC] dark:border-b-[#4A1528]',
  hotspot: 'border-b-[#FCE4EC] dark:border-b-[#4A0018]',
}

const NOTE_SHADOWS: Record<string, string> = {
  domain_event: 'shadow-orange-500/20',
  command: 'shadow-blue-500/20',
  aggregate: 'shadow-yellow-500/20',
  policy: 'shadow-purple-500/20',
  external_system: 'shadow-pink-500/20',
  hotspot: 'shadow-pink-600/20',
}

const NOTE_W = 170
const NOTE_PAD = 12
const NOTE_MIN_H = 90
// Espaçamento vertical entre notas empilhadas na mesma raia (auto-layout).
const NOTE_GAP = 12

function coerceElement(raw: unknown, index: number): EventStormElement {
  const r = raw as Record<string, unknown>
  const lanes = LANES.map((l) => l.id)
  const typeRaw = String(r.type ?? 'domain_event')
  const type = (lanes.includes(typeRaw) ? typeRaw : 'domain_event') as EventStormElement['type']
  let lane = String(r.lane || type)
  if (!lanes.includes(lane)) lane = type
  const textRaw = String(r.text ?? r.label ?? r.description ?? '').trim()
  const text = textRaw.length > 0 ? textRaw : '—'

  // x/y são preservados apenas para o feedback visual durante o arraste; o layout
  // final é automático (empilhamento vertical por raia no render), então NÃO
  // dependemos mais do posicionamento do backend — que causava sobreposição.
  const xNum = typeof r.x === 'number' ? r.x : typeof r.x === 'string' ? parseFloat(r.x) : NaN
  const yNum = typeof r.y === 'number' ? r.y : typeof r.y === 'string' ? parseFloat(r.y) : NaN

  return {
    id: String(r.id || `es-${index}-${Math.random().toString(36).slice(2, 9)}`),
    type,
    text,
    x: Number.isFinite(xNum) ? xNum : NOTE_PAD,
    y: Number.isFinite(yNum) ? yNum : NOTE_PAD,
    lane,
    created_by: String(r.created_by || 'unknown'),
    created_at: String(r.created_at || new Date().toISOString()),
  }
}

interface EventStormCanvasProps {
  elements: Y.Array<any> | EventStormElement[]
  users: UserPresence[]
  currentUserId?: string
  isRegenerating?: boolean
  /** True quando o backend entregou o esqueleto genérico (LLM falhou). */
  isFallback?: boolean
  onRegenerate?: () => void
  onDiscuss?: (text: string) => void
}

export default function EventStormCanvas({ elements, users: _users, currentUserId, isRegenerating = false, isFallback = false, onRegenerate, onDiscuss }: EventStormCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const laneContentRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [elementList, setElementList] = useState<EventStormElement[]>([])
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [selectedLane, setSelectedLane] = useState('domain_event')
  const [newNoteText, setNewNoteText] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [boardView, setBoardView] = useState(true)

  const isYArray = !Array.isArray(elements)
  const getElementsArray = useCallback(() => {
    return isYArray ? (elements as Y.Array<any>).toArray() : (elements as EventStormElement[])
  }, [elements, isYArray])

  useEffect(() => {
    if (Array.isArray(elements)) {
      setElementList(elements.map((e, i) => coerceElement(e as unknown, i)))
      return
    }
    const handler = () =>
      setElementList(elements.toArray().map((e, i) => coerceElement(e as unknown, i)))
    elements.observe(handler)
    handler()
    return () => elements.unobserve(handler)
  }, [elements])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, el: EventStormElement) => {
      e.stopPropagation()
      const cardRect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      setDraggingId(el.id)
      setDragOffset({ x: e.clientX - cardRect.left, y: e.clientY - cardRect.top })
    },
    []
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!draggingId || !containerRef.current) return
      const containerRect = containerRef.current.getBoundingClientRect()
      const cardLeft = e.clientX - dragOffset.x
      const cardTop = e.clientY - dragOffset.y

      const arr = getElementsArray()
      const idx = arr.findIndex((el) => el.id === draggingId)
      if (idx === -1) return

      const el = arr[idx]
      const laneWidth = containerRect.width / LANES.length
      const laneIndex = Math.max(0, Math.min(LANES.length - 1, Math.floor(cardLeft / laneWidth)))
      const newLane = LANES[laneIndex].id

      const laneContentEl = laneContentRefs.current[newLane]
      const laneRect = laneContentEl?.getBoundingClientRect()
      if (!laneRect) return

      const localX = Math.min(
        Math.max(cardLeft - laneRect.left, NOTE_PAD),
        Math.max(laneWidth - NOTE_W - NOTE_PAD, NOTE_PAD),
      )
      const localY = Math.max(cardTop - laneRect.top, NOTE_PAD)

      if (isYArray) {
        const yarr = elements as Y.Array<any>
        yarr.delete(idx, 1)
        yarr.insert(idx, [{ ...el, x: localX, y: localY, lane: newLane }])
      } else {
        const newList = [...arr]
        newList[idx] = { ...el, x: localX, y: localY, lane: newLane }
        setElementList(newList)
      }
    },
    [draggingId, dragOffset, elements, getElementsArray, isYArray]
  )

  const handleMouseUp = useCallback(() => {
    setDraggingId(null)
  }, [])

  const addNote = useCallback(() => {
    if (!newNoteText.trim() || !currentUserId) return
    const lane = LANES.find((l) => l.id === selectedLane) || LANES[0]
    const note: EventStormElement = {
      id: generateId(),
      type: selectedLane as EventStormElement['type'],
      text: newNoteText.trim(),
      x: NOTE_PAD + Math.random() * 40,
      y: NOTE_PAD + Math.random() * 60,
      lane: lane.id,
      color: lane.color,
      created_by: currentUserId,
      created_at: new Date().toISOString(),
    }
    if (isYArray) {
      (elements as Y.Array<any>).push([note])
    } else {
      setElementList((prev) => [...prev, note])
    }
    setNewNoteText('')
    setShowAddForm(false)
  }, [newNoteText, selectedLane, currentUserId, elements, isYArray])

  const deleteNote = useCallback(
    (id: string) => {
      const arr = getElementsArray()
      const idx = arr.findIndex((el) => el.id === id)
      if (idx === -1) return
      if (isYArray) {
        (elements as Y.Array<any>).delete(idx, 1)
      } else {
        setElementList((prev) => prev.filter((_, i) => i !== idx))
      }
    },
    [elements, isYArray, getElementsArray]
  )

  const laneElements = useCallback(
    (laneId: string) => elementList.filter((el) => el.lane === laneId),
    [elementList]
  )

  const totalNotes = elementList.length

  return (
    <div className="w-full h-full flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-500" />
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Event Storming</h2>
          </div>
          <span className="text-xs text-gray-400 dark:text-gray-500 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800">
            {totalNotes} {totalNotes === 1 ? 'nota' : 'notas'}
          </span>
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            <button type="button" onClick={() => setBoardView(true)} className={`px-2 py-1 rounded-md text-xs cursor-pointer ${boardView ? 'bg-white dark:bg-gray-700 font-medium' : 'text-gray-500'}`}>Fluxo</button>
            <button type="button" onClick={() => setBoardView(false)} className={`px-2 py-1 rounded-md text-xs cursor-pointer ${!boardView ? 'bg-white dark:bg-gray-700 font-medium' : 'text-gray-500'}`}>Editar</button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {showAddForm ? (
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <select
                value={selectedLane}
                onChange={(e) => setSelectedLane(e.target.value)}
                className="text-xs bg-transparent border-none outline-none text-gray-700 dark:text-gray-200 px-2"
              >
                {LANES.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.short}
                  </option>
                ))}
              </select>
              <input
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addNote()}
                placeholder="Digite a nota..."
                autoFocus
                className="text-xs bg-white dark:bg-gray-700 rounded px-2 py-1 outline-none text-gray-800 dark:text-gray-100 w-40"
              />
              <button
                onClick={addNote}
                className="p-1 rounded bg-violet-600 hover:bg-violet-500 text-white"
              >
                <Plus size={12} />
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-600 hover:bg-violet-500 text-white transition-colors shadow-sm"
            >
              <Plus size={12} />
              Adicionar Nota
            </button>
          )}
        </div>
      </div>

      {isFallback && totalNotes > 0 && (
        <FallbackNotice artifactName="Event Storm" onRegenerate={onRegenerate} />
      )}

      {boardView ? (
        <StormFlowBoard
          elements={elementList}
          onDiscuss={onDiscuss}
          onAccept={(hotspot) => {
            if (!(elements instanceof Y.Array)) return
            const list = elements.toArray() as EventStormElement[]
            const idx = list.findIndex((el) => el.id === hotspot.id)
            if (idx >= 0) elements.delete(idx, 1)
            elements.push([{
              id: generateId(),
              type: 'aggregate',
              text: hotspot.text.replace(/\?+$/, '').trim() || hotspot.text,
              x: hotspot.x,
              y: hotspot.y,
              lane: 'aggregate',
              created_by: currentUserId || 'user',
              created_at: new Date().toISOString(),
            }])
          }}
        />
      ) : (
      <div
        ref={containerRef}
        className="flex-1 overflow-auto relative select-none"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <ArtifactStateOverlay
          isRegenerating={isRegenerating}
          hasContent={totalNotes > 0}
          label="Recalculando mapa de Event Storm"
          hint="Eventos, comandos, agregados e políticas serão atualizados."
        />
        {/* Legend */}
        <div className="absolute top-3 right-3 z-20 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2.5">
          <div className="flex items-center gap-1.5 mb-2">
            <Info size={10} className="text-gray-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Legenda
            </span>
          </div>
          <div className="space-y-1.5">
            {LANES.map((lane) => {
              const Icon = lane.icon
              return (
                <div key={lane.id} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-sm shadow-sm shrink-0"
                    style={{ backgroundColor: lane.color }}
                  />
                  <Icon size={10} style={{ color: lane.color }} />
                  <span className="text-[10px] text-gray-600 dark:text-gray-300">{lane.label}</span>
                </div>
              )
            })}
          </div>
        </div>

        <div className="flex h-full min-h-[600px]">
          {LANES.map((lane) => {
            const LaneIcon = lane.icon
            const items = laneElements(lane.id)
            return (
              <div
                key={lane.id}
                className={`flex-1 min-w-[180px] border-r border-gray-200 dark:border-gray-800 ${lane.bg} relative`}
              >
                {/* Lane Header */}
                <div
                  className={`sticky top-0 z-10 px-3 py-2.5 ${lane.headerBg} flex items-center gap-2 shadow-sm`}
                >
                  <LaneIcon size={14} className="text-white drop-shadow-sm" />
                  <span className="text-xs font-bold text-white drop-shadow-sm">{lane.label}</span>
                  <span className="ml-auto text-[10px] font-medium text-white/90 bg-white/20 px-1.5 py-0.5 rounded">
                    {items.length}
                  </span>
                </div>

                {/* Lane Content — auto-layout vertical: notas empilhadas por ordem,
                    sem sobreposição. A altura acompanha a quantidade de notas. */}
                <div
                  ref={(el) => {
                    laneContentRefs.current[lane.id] = el
                  }}
                  className="p-3 relative"
                  style={{ minHeight: Math.max(200, NOTE_PAD + items.length * (NOTE_MIN_H + NOTE_GAP)) }}
                >
                  {items.map((el, itemIndex) => {
                    const isDragging = draggingId === el.id
                    // Enquanto arrasta, segue o cursor (x/y temporário); parado,
                    // encaixa na pilha da raia pela sua ordem.
                    const noteLeft = isDragging ? el.x : NOTE_PAD
                    const noteTop = isDragging ? el.y : NOTE_PAD + itemIndex * (NOTE_MIN_H + NOTE_GAP)
                    return (
                    <div
                      key={el.id}
                      onMouseDown={(e) => handleMouseDown(e, el)}
                      className={`group absolute cursor-grab active:cursor-grabbing rounded-sm transition-all duration-150 ${NOTE_BG[el.type]} ${NOTE_SHADOWS[el.type]} ${isDragging ? 'shadow-xl scale-105 z-50 cursor-grabbing' : 'shadow-md hover:shadow-lg'} ${el.type === 'hotspot' ? 'rotate-[8deg]' : ''}`}
                      style={{
                        left: noteLeft,
                        top: noteTop,
                        width: NOTE_W,
                        minHeight: NOTE_MIN_H,
                      }}
                    >
                      {/* Folded corner - shadow overlay */}
                      <div className="absolute bottom-0 right-0 w-5 h-5 bg-gradient-to-tl from-black/10 to-transparent dark:from-black/30 pointer-events-none rounded-bl-sm" />
                      {/* Folded corner - triangle */}
                      <div
                        className={`absolute bottom-0 right-0 w-0 h-0 border-b-[16px] border-r-[16px] border-r-transparent opacity-70 ${NOTE_FOLD[el.type]}`}
                      />

                      <div className="p-3 pb-5">
                        {/* Note header with type badge */}
                        <div className="flex items-center gap-1.5 mb-2">
                          <GripVertical
                            size={10}
                            className="text-black/20 dark:text-white/20 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          />
                          <span
                            className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{
                              backgroundColor: lane.color + '20',
                              color: lane.color,
                            }}
                          >
                            {lane.short}
                          </span>
                          {lane.hint && (
                            <span className="text-[8px] italic text-black/30 dark:text-white/30">
                              {lane.hint}
                            </span>
                          )}
                        </div>

                        {/* Note text */}
                        <p
                          className={`text-sm font-medium leading-snug font-mono break-words ${lane.text}`}
                          style={{ maxWidth: NOTE_W - NOTE_PAD * 2 }}
                        >
                          {el.text}
                        </p>

                        {/* Footer with agent badge and delete */}
                        <div className="absolute bottom-1.5 left-3 right-3 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-[8px] text-black/30 dark:text-white/30">
                            {el.created_by === 'agent' ? '🤖 Agente' : '👤 Você'}
                          </span>
                          {el.created_by === currentUserId && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                deleteNote(el.id)
                              }}
                              className="p-0.5 rounded hover:bg-red-100/50 dark:hover:bg-red-900/30 text-red-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 size={10} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    )
                  })}

                  {items.length === 0 && (
                    <div className="flex items-center justify-center h-32 text-gray-300 dark:text-gray-600 text-xs">
                      Arraste notas para aqui
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      )}
    </div>
  )
}

function StormFlowBoard({
  elements,
  onAccept,
  onDiscuss,
}: {
  elements: EventStormElement[]
  onAccept?: (hotspot: EventStormElement) => void
  onDiscuss?: (text: string) => void
}) {
  const xs = elements.map((el) => el.x)
  const min = xs.length ? Math.min(...xs) : 0
  const max = xs.length ? Math.max(...xs) : 1
  const span = Math.max(max - min, 1)
  const cols = Math.min(6, Math.max(2, Math.round(span / 180) || 2))
  const colOf = (el: EventStormElement) => Math.min(cols, Math.max(1, Math.floor(((el.x - min) / span) * cols) + 1))

  const hotspot = elements.find((el) => el.type === 'hotspot')

  return (
    <div className="flex-1 overflow-auto bg-canvas p-4">
      <div className="flex flex-col gap-1.5 w-max min-w-full">
        <div className="grid gap-2 font-mono text-[10.5px] uppercase tracking-wide text-faint" style={{ gridTemplateColumns: `130px repeat(${cols}, 164px)` }}>
          <span />
          {Array.from({ length: cols }, (_, i) => (
            <span key={i}>0{i + 1}</span>
          ))}
        </div>
        {LANES.map((lane) => {
          const items = elements.filter((el) => el.lane === lane.id || el.type === lane.id)
          return (
            <div key={lane.id} className="grid gap-2 items-stretch min-h-[74px] border-t border-dashed border-line pt-1.5" style={{ gridTemplateColumns: `130px repeat(${cols}, 164px)` }}>
              <div className="flex items-start gap-1.5 pt-1 text-xs font-semibold text-muted">
                <span className="w-2.5 h-2.5 rounded-sm mt-0.5" style={{ background: lane.color }} />
                {lane.short}
              </div>
              {items.map((item) => (
                <div
                  key={item.id}
                  className="rounded-sm px-2 py-2 text-xs font-medium min-h-[62px] flex flex-col justify-between"
                  style={{ gridColumn: colOf(item) + 1, background: lane.color, color: '#1a1a1a' }}
                >
                  <span>{item.text}</span>
                  <span className="font-mono text-[10px] opacity-70">{item.created_by === 'agent' ? 'Jobs' : 'edição humana'}</span>
                </div>
              ))}
            </div>
          )
        })}
      </div>
      {hotspot && (
        <div className="mt-4 max-w-md bg-surface border border-line rounded-xl p-3.5 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-warn">Jobs · hotspot</div>
          <p className="text-sm mt-1.5 mb-3">{hotspot.text}</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => onAccept?.(hotspot)} className="h-8 px-2.5 rounded-md bg-ink text-surface text-xs font-semibold cursor-pointer">
              Adicionar
            </button>
            <button type="button" onClick={() => onDiscuss?.(hotspot.text)} className="h-8 px-2.5 rounded-md border border-line text-xs cursor-pointer">
              Discutir no chat
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
